/**
 * The arithmetic behind a chart, kept apart from the drawing so it can be
 * tested without a DOM.
 */

/** Round tick values that cover [min, max], about `target` of them. */
export function niceTicks(min: number, max: number, target = 4): number[] {
  if (min === max) {
    max = min === 0 ? 1 : min + Math.abs(min) * 0.1;
    min = min === 0 ? 0 : min - Math.abs(min) * 0.1;
  }

  const span = niceNumber(max - min, false);
  const step = niceNumber(span / Math.max(target, 1), true);
  const first = Math.floor(min / step) * step;
  const last = Math.ceil(max / step) * step;

  const ticks: number[] = [];
  // Multiplying instead of accumulating keeps 0.1 + 0.2 out of the labels.
  for (let i = 0; first + i * step <= last + step / 2; i++) {
    ticks.push(Number((first + i * step).toPrecision(12)));
  }
  return ticks;
}

function niceNumber(value: number, round: boolean): number {
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / 10 ** exponent;

  let nice: number;
  if (round) {
    nice = fraction < 1.5 ? 1 : fraction < 3 ? 2 : fraction < 7 ? 5 : 10;
  } else {
    nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  }
  return nice * 10 ** exponent;
}

/** Which of `count` labels to print so they do not collide: at most `max`. */
export function labelStride(count: number, max: number): number {
  return Math.max(1, Math.ceil(count / max));
}

export function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export interface Slice {
  index: number;
  value: number;
  share: number;
  start: number;
  end: number;
}

/** Angles, in radians from twelve o'clock, for each non-empty slice. */
export function sliceAngles(values: number[]): Slice[] {
  const total = values.reduce((sum, v) => sum + v, 0);
  const slices: Slice[] = [];
  let angle = 0;

  values.forEach((value, index) => {
    if (value <= 0) return;
    const share = value / total;
    slices.push({ index, value, share, start: angle, end: angle + share * Math.PI * 2 });
    angle += share * Math.PI * 2;
  });

  return slices;
}

/** SVG path for a ring segment. A full circle is split in two, which an arc cannot draw. */
export function ringPath(
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  start: number,
  end: number
): string {
  const full = end - start >= Math.PI * 2 - 1e-6;
  if (full) {
    const half = Math.PI;
    return `${ringPath(cx, cy, outer, inner, start, start + half)} ${ringPath(cx, cy, outer, inner, start + half, start + half * 2 - 1e-4)}`;
  }

  const point = (r: number, a: number) =>
    `${(cx + r * Math.sin(a)).toFixed(2)} ${(cy - r * Math.cos(a)).toFixed(2)}`;
  const large = end - start > Math.PI ? 1 : 0;

  return [
    `M ${point(outer, start)}`,
    `A ${outer} ${outer} 0 ${large} 1 ${point(outer, end)}`,
    `L ${point(inner, end)}`,
    `A ${inner} ${inner} 0 ${large} 0 ${point(inner, start)}`,
    'Z',
  ].join(' ');
}

/** A bar anchored at the baseline with a rounded data end and a square base. */
export function barPath(x: number, baseline: number, valueY: number, width: number, r = 4): string {
  const height = Math.abs(valueY - baseline);
  const radius = Math.min(r, width / 2, height);
  const up = valueY <= baseline;

  if (height === 0) return '';

  if (up) {
    return [
      `M ${x} ${baseline}`,
      `V ${valueY + radius}`,
      `Q ${x} ${valueY} ${x + radius} ${valueY}`,
      `H ${x + width - radius}`,
      `Q ${x + width} ${valueY} ${x + width} ${valueY + radius}`,
      `V ${baseline}`,
      'Z',
    ].join(' ');
  }

  return [
    `M ${x} ${baseline}`,
    `V ${valueY - radius}`,
    `Q ${x} ${valueY} ${x + radius} ${valueY}`,
    `H ${x + width - radius}`,
    `Q ${x + width} ${valueY} ${x + width} ${valueY - radius}`,
    `V ${baseline}`,
    'Z',
  ].join(' ');
}
